import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton as _IconButton,
  TextField,
  Typography,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import { Edit as _Edit, ArrowDropDown } from '@mui/icons-material';
import StatusChip from '../StatusChip';
import { CaseStatus, getCaseStatusLabel } from '../../utils/caseStatusStyles';

interface CaseHeaderProps {
  data: any;
  caseId?: string;
  editingLeadDefendant: boolean;
  leadDefendantValue: string;
  onEditLeadDefendant: (editing: boolean) => void;
  onLeadDefendantChange: (value: string) => void;
  onSaveLeadDefendant: () => void;
  onCancelEditLeadDefendant: () => void;
  editingCaseNumber?: boolean;
  caseNumberValue?: string;
  onEditCaseNumber?: (editing: boolean) => void;
  onCaseNumberChange?: (value: string) => void;
  onSaveCaseNumber?: () => void;
  onCancelEditCaseNumber?: () => void;
  onStatusChange?: (status: CaseStatus) => void;
  userRole?: string;
  formatMoney: (amount: number) => string;
  demoMode?: boolean;
}

export const CaseHeader: React.FC<CaseHeaderProps> = ({
  data,
  caseId,
  editingLeadDefendant,
  leadDefendantValue,
  onEditLeadDefendant,
  onLeadDefendantChange,
  onSaveLeadDefendant,
  onCancelEditLeadDefendant,
  editingCaseNumber,
  caseNumberValue,
  onEditCaseNumber,
  onCaseNumberChange,
  onSaveCaseNumber,
  onCancelEditCaseNumber,
  onStatusChange,
  userRole,
  formatMoney: _formatMoney,
  demoMode = false
}) => {
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const statusMenuOpen = Boolean(statusMenuAnchor);

  const allStatuses: CaseStatus[] = ['IN_REVIEW', 'UNDER_INVESTIGATION', 'FILED', 'CLOSED'];

  // Only allow status changes for non-CASE_REVIEWER users
  const canEditStatus = caseId && onStatusChange && userRole !== 'CASE_REVIEWER';

  // Only allow editing case details (lead defendant, case number) for non-CASE_REVIEWER users
  const canEditCaseDetails = caseId && userRole !== 'CASE_REVIEWER';

  const handleStatusClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (canEditStatus) {
      setStatusMenuAnchor(event.currentTarget);
    }
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
  };

  const handleStatusSelect = (status: CaseStatus) => {
    if (onStatusChange) {
      onStatusChange(status);
    }
    handleStatusMenuClose();
  };

  return (
    <Box
      sx={{
        px: 4,
        py: 3.5,
        bgcolor: 'background.paper',
        borderBottom: 2,
        borderColor: 'primary.main',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      {/* Title Row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {caseId ? (
          // For cases, show editable lead defendant as main title
          editingLeadDefendant ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mr: 2 }}>
              <TextField
                size="small"
                value={leadDefendantValue}
                onChange={(e) => onLeadDefendantChange(e.target.value)}
                sx={{ fontSize: '1.5rem', fontWeight: 600, width: 500 }}
                autoFocus
              />
              <Button size="small" onClick={onSaveLeadDefendant} disabled={!leadDefendantValue.trim()}>
                Save
              </Button>
              <Button size="small" onClick={onCancelEditLeadDefendant}>
                Cancel
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  cursor: canEditCaseDetails ? 'pointer' : 'default',
                  '&:hover': canEditCaseDetails ? {
                    color: 'primary.main',
                  } : {}
                }}
                onClick={() => canEditCaseDetails && onEditLeadDefendant(true)}
              >
                {data.lead_defendant || data.name || 'Unnamed Case'}
              </Typography>

              {/* Divider - hidden in demo mode */}
              {caseId && !demoMode && (
                <Divider orientation="vertical" flexItem sx={{ height: 24, alignSelf: 'center' }} />
              )}

              {/* Case Number Field - hidden in demo mode */}
              {caseId && !demoMode && (
                editingCaseNumber ? (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      value={caseNumberValue}
                      onChange={(e) => onCaseNumberChange?.(e.target.value)}
                      placeholder="Case Number"
                      autoFocus
                      sx={{ width: 200 }}
                    />
                    <Button size="small" onClick={onSaveCaseNumber}>
                      Save
                    </Button>
                    <Button size="small" onClick={onCancelEditCaseNumber}>
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.95rem',
                      cursor: canEditCaseDetails ? 'pointer' : 'default',
                      '&:hover': canEditCaseDetails ? {
                        color: 'primary.main',
                      } : {}
                    }}
                    onClick={() => canEditCaseDetails && onEditCaseNumber?.(true)}
                  >
                    {data.case_number ? `Case #: ${data.case_number}` : 'Add case number'}
                  </Typography>
                )
              )}
            </Box>
          )
        ) : (
          // For case scoping, show the target name
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {data.name}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            {data.scheme?.name}
          </Typography>
          <Box
            onClick={handleStatusClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: canEditStatus ? 'pointer' : 'default',
              '&:hover': canEditStatus ? { opacity: 0.8 } : {}
            }}
          >
            <StatusChip status={data.status as CaseStatus} size="medium" />
            {canEditStatus && <ArrowDropDown />}
          </Box>

          <Menu
            anchorEl={statusMenuAnchor}
            open={statusMenuOpen}
            onClose={handleStatusMenuClose}
          >
            {allStatuses.map((status) => (
              <MenuItem
                key={status}
                onClick={() => handleStatusSelect(status)}
                selected={status === data.status}
              >
                {getCaseStatusLabel(status)}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>
    </Box>
  );
};